import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AppState } from '@store/app.state';
import { PaymentActions } from '@store/actions/payment.actions';
import { PaymentConfigStatus, HasPaidStatus } from '@store/reducers/payment.reducer';
import {
  selectConfigStatus,
  selectConfigError,
  selectClientToken,
  selectPriceId,
  selectIsSandbox,
  selectHasPaidStatus,
  selectHasPaid,
} from '@store/selectors/payment.selectors';
import { AuthService } from '@services/auth.service';

type HomeView = 'home' | 'privacy-policy' | 'terms' | 'pricing';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './home.html',
  styleUrls: ['./home.css'],
})
export class HomePage implements OnInit, OnDestroy {
  theme: 'pxlvd' | 'dark' | 'light' = 'pxlvd';
  view: HomeView = 'home';

  // ── Store selectors (exposed to template via async pipe) ──────
  // Assigned in constructor so constructor-injected `store` is available.
  configStatus$!:  Observable<PaymentConfigStatus>;
  configError$!:   Observable<string | null>;
  isSandbox$!:     Observable<boolean>;
  hasPaid$!:       Observable<boolean>;
  hasPaidStatus$!: Observable<HasPaidStatus>;

  // ── Paddle UI state (local - comes from Paddle SDK, not our API) ──
  priceDisplay    = '···';
  priceNote       = '';
  checkoutLoading = false;

  private _paddleLoaded      = false;
  private _paddleInitialised = false;
  private _destroy$          = new Subject<void>();

  constructor(
    private router: Router,
    public auth: AuthService,
    private store: Store<AppState>,
  ) {
    // Assign here so `this.store` is guaranteed to be initialised
    this.configStatus$  = this.store.select(selectConfigStatus);
    this.configError$   = this.store.select(selectConfigError);
    this.isSandbox$     = this.store.select(selectIsSandbox);
    this.hasPaid$       = this.store.select(selectHasPaid);
    this.hasPaidStatus$ = this.store.select(selectHasPaidStatus);
  }

  ngOnInit(): void {
    const saved = localStorage.getItem('pxlvd-theme') as any;
    this.theme = saved || 'pxlvd';
    document.body.setAttribute('data-theme', this.theme);
    this.syncViewFromUrl(this.router.url);

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this._destroy$),
    ).subscribe((e: any) => {
      this.syncViewFromUrl(e.urlAfterRedirects ?? '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // When Paddle config lands in the store, initialise Paddle SDK + get price
    this.store.select(selectConfigStatus).pipe(
      filter(s => s === 'ready'),
      withLatestFrom(
        this.store.select(selectClientToken),
        this.store.select(selectPriceId),
        this.store.select(selectIsSandbox),
      ),
      take(1),
      takeUntil(this._destroy$),
    ).subscribe(async ([, clientToken, priceId, isSandbox]) => {
      if (!clientToken || !priceId) return;
      await this._initialisePaddle(clientToken, priceId, isSandbox);
    });

    // When a user signs in while on the pricing view, dispatch checkHasPaid
    this.auth.state$.pipe(
      filter(s => s.initialized && !!s.user),
      takeUntil(this._destroy$),
    ).subscribe(() => {
      if (this.view === 'pricing') {
        this.store.select(selectHasPaidStatus).pipe(take(1)).subscribe(status => {
          if (status === 'idle') {
            this.store.dispatch(PaymentActions.checkHasPaid());
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private syncViewFromUrl(url: string): void {
    if (url.includes('privacy-policy')) { this.view = 'privacy-policy'; return; }
    if (url.includes('terms'))          { this.view = 'terms';           return; }
    if (url.includes('pricing'))        { this.view = 'pricing'; this._activatePricing(); return; }
    this.view = 'home';
  }

  // ── Pricing activation ────────────────────────────────────────

  /** Dispatches store actions to load config and check payment status.
   *  Guards against re-dispatching when data is already loaded or in-flight. */
  private _activatePricing(): void {
    this.store.select(selectConfigStatus).pipe(take(1)).subscribe(status => {
      if (status === 'idle' || status === 'error') {
        // Reset Paddle initialisation so it re-runs with fresh config
        this._paddleInitialised = false;
        this.priceDisplay = '···';
        this.priceNote = '';
        this.store.dispatch(PaymentActions.loadConfig());
      }
    });

    if (this.auth.user) {
      this.store.select(selectHasPaidStatus).pipe(take(1)).subscribe(status => {
        if (status === 'idle') {
          this.store.dispatch(PaymentActions.checkHasPaid());
        }
      });
    }
  }

  // ── Paddle SDK (local - not NgRx) ─────────────────────────────

  private _loadPaddleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any)['Paddle']) { this._paddleLoaded = true; resolve(); return; }
      if (this._paddleLoaded)        { resolve(); return; }
      const s = document.createElement('script');
      s.src     = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      s.onload  = () => { this._paddleLoaded = true; resolve(); };
      s.onerror = () => reject(new Error('Failed to load Paddle.js'));
      document.head.appendChild(s);
    });
  }

  private async _initialisePaddle(
    clientToken: string,
    priceId: string,
    isSandbox: boolean,
  ): Promise<void> {
    if (this._paddleInitialised) return;
    try {
      await this._loadPaddleScript();
      const P = (window as any)['Paddle'];
      if (isSandbox) P.Environment.set('sandbox');
      P.Setup({ token: clientToken });
      this._paddleInitialised = true;

      const preview  = await P.PricePreview({ items: [{ priceId, quantity: 1 }] });
      const lineItem = preview.data.details.lineItems[0];
      this.priceDisplay = lineItem.formattedTotals.total;
      this.priceNote    = preview.data.currencyCode;
    } catch (err) {
      console.error('Paddle init error:', err);
      // configError$ is already set by the store if config failed;
      // Paddle errors here are non-critical (price will just stay as '···')
    }
  }

  // ── Checkout ──────────────────────────────────────────────────

  async openCheckout(): Promise<void> {
    if (!this.auth.user) return;

    // Read config from store synchronously
    let clientToken: string | null = null;
    let priceId: string | null = null;
    this.store.select(selectClientToken).pipe(take(1)).subscribe(v => clientToken = v);
    this.store.select(selectPriceId).pipe(take(1)).subscribe(v => priceId = v);

    if (!clientToken || !priceId || !this._paddleInitialised) return;

    this.checkoutLoading = true;
    try {
      const successUrl = window.location.origin + '/payment?success=true';
      const P = (window as any)['Paddle'];
      P.Checkout.open({
        items:      [{ priceId, quantity: 1 }],
        customer:   this.auth.user.email ? { email: this.auth.user.email } : undefined,
        customData: { userId: this.auth.user.id },
        successUrl,
        settings: { theme: 'dark', locale: 'en', successUrl },
      });
    } finally {
      this.checkoutLoading = false;
    }
  }

  // ── Auth ──────────────────────────────────────────────────────

  requestLogin(): void {
    this.auth.requestLogin();
  }

  // ── Navigation ────────────────────────────────────────────────

  setTheme(t: 'pxlvd' | 'dark' | 'light'): void {
    this.theme = t;
    localStorage.setItem('pxlvd-theme', t);
    document.body.setAttribute('data-theme', t);
  }

  navigate(view: HomeView): void {
    this.router.navigate(['/home', ...(view === 'home' ? [] : [view])]);
  }
}
